// =========================================================
// frontend/js/report.js - Enterprise Jahres-Impact-Report (V11.6 - Full & Audit-Safe)
// =========================================================

window.generateYearlyStats = function(year) {
    const allUsers = window.usersList || [];
    const allEvents = window.events || [];
    
    let monthlyData = [];
    let nodeImpacts = {}; 
    let yearlyTotal = {
        co2Brutto: 0, co2Netto: 0, leakage: 0,
        kmSaved: 0, hoDaysTotal: 0, moneySaved: 0 // Money hinzugefügt
    };

    for (let m = 0; m <= 11; m++) {
        let monthStats = { 
            monthName: new Date(year, m).toLocaleString('de-DE', { month: 'long' }),
            co2Netto: 0, co2Brutto: 0, leakage: 0, days: 0 
        };

        const monthEvents = allEvents.filter(e => {
            if(!e.date) return false;
            const d = new Date(e.date);
            const type = (e.type || '').toUpperCase();
            return (type === 'HOMEOFFICE' || type === 'HO') && 
                   e.status === 'APPROVED' && 
                   d.getMonth() === m && d.getFullYear() === parseInt(year);
        });

        allUsers.forEach(user => {
            const userMonthEvents = monthEvents.filter(e => e.userId === user.uid);
            
            if (userMonthEvents.length > 0) {
                userMonthEvents.forEach(event => {
                    
                    // --- ZENTRALE LOGIK-ABFRAGE (Synchron zu Stats & Dashboard) ---
                    // Wir simulieren das Datum des Events für die Heizperioden-Prüfung
                    const eventDate = new Date(event.date);
                    const impact = window.calculateImpact(user.commute, 1, event.co2SavedAtBooking);

                    // Werte zurück in Zahlen wandeln für die Summenbildung
                    const netto = parseFloat(impact.co2Netto);
                    const brutto = parseFloat(impact.co2Brutto);
                    const leak = parseFloat(impact.leakage);
                    const money = parseFloat(impact.money);
                    const dist = parseFloat(user.commute?.distance || 0);

                    // Monatliche Summen
                    monthStats.co2Netto += netto;
                    monthStats.co2Brutto += brutto;
                    monthStats.leakage += leak;
                    monthStats.days += 1;

                    // Jährliche Summen
                    yearlyTotal.co2Netto += netto;
                    yearlyTotal.co2Brutto += brutto;
                    yearlyTotal.leakage += leak;
                    yearlyTotal.kmSaved += (dist * 2);
                    yearlyTotal.hoDaysTotal += 1;
                    yearlyTotal.moneySaved += money;

                    // --- STRUKTUR-AGGREGATION (Nach NodeId) ---
                    const nid = user.nodeId || 'unassigned';
                    if (!nodeImpacts[nid]) nodeImpacts[nid] = { co2: 0, days: 0 };
                    nodeImpacts[nid].co2 += netto;
                    nodeImpacts[nid].days += 1;
                });
            }
        });
        monthlyData.push(monthStats);
    }

    return { monthlyData, yearlyTotal, nodeImpacts };
};

/**
 * Hilfsfunktion: Rekursives Rendern der Impact-Struktur
 */
window.renderReportNodeRecursive = function(node, nodeImpacts, depth = 0) {
    if (!node) return { html: '', co2: 0, days: 0 };

    const impact = nodeImpacts[node.id] || { co2: 0, days: 0 };
    
    // Wir berechnen auch den Rollup (Summe der Kinder)
    let cumulativeCO2 = impact.co2;
    let cumulativeDays = impact.days;
    
    const childrenHTML = (node.children || []).map(child => {
        const childRes = window.renderReportNodeRecursive(child, nodeImpacts, depth + 1);
        cumulativeCO2 += childRes.co2;
        cumulativeDays += childRes.days;
        return childRes.html;
    }).join('');

    // Nur anzeigen, wenn CO2 gespart wurde oder es eine Haupt-Ebene ist
    if (cumulativeCO2 === 0 && depth > 0) return { html: '', co2: 0, days: 0 };

    const indent = depth * 24;
    const typeStr = (node.type || 'default').toLowerCase();
    const iconMap = { 'building': 'building-2', 'unit': 'layers', 'team': 'users', 'project': 'briefcase', 'default': 'circle' };
    const currentIcon = iconMap[typeStr] || iconMap['default'];

    const html = `
        <div class="mb-2">
            <div class="flex items-center justify-between bg-white border border-slate-100 p-4 rounded-2xl shadow-sm" style="margin-left: ${indent}px">
                <div class="flex items-center gap-3">
                    <div class="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-50 text-slate-400">
                        <i data-lucide="${currentIcon}" class="w-4 h-4"></i>
                    </div>
                    <div>
                        <p class="font-black text-slate-700 text-sm leading-tight">${node.name || 'Unbenannte Ebene'}</p>
                        <p class="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">${node.type || 'Ebene'}</p>
                    </div>
                </div>
                <div class="text-right">
                    <p class="font-black text-emerald-600 text-sm">${cumulativeCO2.toFixed(1)} kg</p>
                    <p class="text-[9px] font-bold text-slate-400 uppercase tracking-widest">${cumulativeDays} Tage</p>
                </div>
            </div>
            ${childrenHTML}
        </div>
    `;

    return { html, co2: cumulativeCO2, days: cumulativeDays };
};

window.renderReportHTML = function() {
    const currentYear = new Date().getFullYear();
    const { monthlyData, yearlyTotal, nodeImpacts } = window.generateYearlyStats(currentYear);
    const structure = window._secureTreeMemory || window.orgStructure || [];

    // Sicherstellen, dass maxCo2 niemals 0 oder NaN ist, um Darstellungsfehler im Chart zu vermeiden
    const maxCo2 = Math.max(...monthlyData.map(m => Math.max(0, m.co2Netto)), 1);

    return `
        <div class="p-8 lg:p-12 max-w-6xl mx-auto animate-fadeIn pb-24">
            <div class="flex justify-between items-center mb-12">
                <div>
                    <h2 class="text-5xl font-black text-slate-800 tracking-tighter italic uppercase">Impact Report</h2>
                    <p class="text-slate-400 font-bold uppercase text-[11px] tracking-[0.2em] mt-2">Scope 3 Emissions: Employee Commuting (GHG-Protocol)</p>
                </div>
                <button onclick="window.print()" class="bg-white border border-slate-200 text-slate-600 px-6 py-3 rounded-2xl text-[10px] font-black uppercase hover:bg-slate-50 transition-all flex items-center gap-2 shadow-sm">
                    <i data-lucide="printer" class="w-4 h-4"></i> Drucken
                </button>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                <div class="bg-emerald-600 p-8 rounded-[2.5rem] text-white shadow-xl shadow-emerald-100 relative overflow-hidden group">
                    <i data-lucide="wind" class="absolute -right-4 -bottom-4 w-32 h-32 opacity-20 rotate-12"></i>
                    <p class="text-[10px] font-black uppercase opacity-70 mb-2 relative z-10">Jahres-CO₂ Netto Ersparnis</p>
                    <h3 class="text-5xl font-black relative z-10">${yearlyTotal.co2Netto.toFixed(1)} <span class="text-xl opacity-60">kg</span></h3>
                </div>
                <div class="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm relative overflow-hidden group">
                    <i data-lucide="tree-pine" class="absolute -right-4 -bottom-4 w-32 h-32 text-emerald-50 opacity-10 rotate-12"></i>
                    <p class="text-[10px] font-black text-slate-400 uppercase mb-2 relative z-10">Wald-Äquivalent</p>
                    <h3 class="text-5xl font-black text-slate-800 relative z-10">${(yearlyTotal.co2Netto / 12.5).toFixed(1)} <span class="text-xl text-slate-300">Bäume</span></h3>
                </div>
            </div>

            <div class="mb-12">
                <div class="flex items-center gap-4 mb-8">
                    <div class="h-[2px] flex-1 bg-slate-100"></div>
                    <h4 class="text-xs font-black text-slate-400 uppercase tracking-[0.3em]">Impact nach Struktur</h4>
                    <div class="h-[2px] flex-1 bg-slate-100"></div>
                </div>
                
                <div class="space-y-4">
                    ${structure.length > 0 
                        ? structure.map(node => window.renderReportNodeRecursive(node, nodeImpacts).html).join('')
                        : '<p class="text-center text-slate-400 py-8">Keine Strukturdaten verfügbar.</p>'
                    }
                    
                    ${nodeImpacts['unassigned'] ? `
                        <div class="mt-4 opacity-60">
                             <div class="flex items-center justify-between bg-slate-50 border border-slate-100 p-4 rounded-2xl">
                                <div class="flex items-center gap-3">
                                    <div class="w-8 h-8 flex items-center justify-center rounded-lg bg-white text-slate-300">
                                        <i data-lucide="help-circle" class="w-4 h-4"></i>
                                    </div>
                                    <p class="font-bold text-slate-500 text-sm">Nicht zugeordnet</p>
                                </div>
                                <div class="text-right">
                                    <p class="font-black text-slate-500 text-sm">${nodeImpacts['unassigned'].co2.toFixed(1)} kg</p>
                                    <p class="text-[9px] font-bold text-slate-400 uppercase tracking-widest">${nodeImpacts['unassigned'].days} Tage</p>
                                </div>
                             </div>
                        </div>
                    ` : ''}
                </div>
            </div>

            <div class="bg-white border border-slate-100 p-10 rounded-[3rem] mb-12 shadow-sm">
                <h4 class="text-xs font-black text-slate-800 uppercase tracking-widest mb-10">Verlauf CO₂ Ersparnis (Netto)</h4>
                <div class="flex items-end justify-between h-56 gap-2 px-2">
                    ${monthlyData.map(m => {
                        const safeNetto = Math.max(0, m.co2Netto);
                        const heightPercent = Math.max((safeNetto / maxCo2) * 100, 2);
                        return `
                        <div class="flex-1 flex flex-col items-center group relative h-full justify-end">
                            <div class="absolute -top-10 opacity-0 group-hover:opacity-100 transition-all bg-slate-800 text-white text-[10px] font-bold px-3 py-1.5 rounded-lg shadow-lg z-20 pointer-events-none whitespace-nowrap">
                                ${m.co2Netto.toFixed(1)} kg
                            </div>
                            <div class="w-full max-w-[40px] bg-emerald-500/10 rounded-t-xl transition-all duration-300 group-hover:bg-emerald-500/20 relative" 
                                 style="height: ${heightPercent}%;">
                                 <div class="absolute top-0 w-full h-1.5 bg-emerald-500 rounded-t-xl"></div>
                            </div>
                            <p class="text-[9px] font-black text-slate-400 mt-4 uppercase">${m.monthName.substring(0, 3)}</p>
                        </div>
                        `;
                    }).join('')}
                </div>
            </div>

            <details class="group bg-white border border-slate-200 rounded-[3rem] shadow-sm overflow-hidden" open>
                <summary class="p-8 cursor-pointer list-none outline-none flex justify-between items-center bg-slate-50/50 hover:bg-slate-50 transition-colors">
                    <div>
                        <h3 class="text-2xl font-black text-slate-800">Geschäftsjahr ${currentYear}</h3>
                        <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">Detailaufschlüsselung nach Monaten aufklappen</p>
                    </div>
                    <div class="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm border border-slate-100 group-open:rotate-180 transition-transform duration-300">
                        <i data-lucide="chevron-down" class="w-6 h-6 text-slate-400"></i>
                    </div>
                </summary>
                
                <div class="p-8 border-t border-slate-100 space-y-4 bg-white">
                    ${monthlyData.map(m => {
                        if (m.days === 0) return '';
                        return `
                        <details class="group/month bg-slate-50 border border-slate-100 rounded-[2rem] overflow-hidden transition-all hover:border-slate-300">
                            <summary class="flex items-center justify-between p-6 cursor-pointer list-none outline-none">
                                <div class="flex items-center gap-6">
                                    <span class="w-24 font-black text-slate-700">${m.monthName}</span>
                                    <span class="bg-blue-100 text-blue-600 text-[10px] font-black px-3 py-1.5 rounded-xl uppercase">${m.days} HO-Tage</span>
                                </div>
                                <div class="flex items-center gap-8">
                                    <div class="text-right">
                                        <p class="text-[9px] font-bold text-slate-400 uppercase">Netto Impact</p>
                                        <p class="font-black text-emerald-600">${m.co2Netto.toFixed(1)} kg</p>
                                    </div>
                                    <i data-lucide="chevron-down" class="w-5 h-5 text-slate-300 group-open/month:rotate-180 transition-transform"></i>
                                </div>
                            </summary>
                            <div class="p-6 pt-2 border-t border-slate-100 bg-white">
                                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div class="p-4 rounded-2xl bg-slate-50">
                                        <p class="text-[9px] font-bold text-slate-400 uppercase mb-1">Vermeidung (Brutto)</p>
                                        <p class="text-xl font-black text-slate-800">+ ${m.co2Brutto.toFixed(1)} kg</p>
                                    </div>
                                    <div class="p-4 rounded-2xl bg-slate-50">
                                        <p class="text-[9px] font-bold text-slate-400 uppercase mb-1">Leakage (HO-Verbrauch)</p>
                                        <p class="text-xl font-black text-rose-500">- ${m.leakage.toFixed(1)} kg</p>
                                    </div>
                                    <div class="p-4 rounded-2xl bg-slate-50">
                                        <p class="text-[9px] font-bold text-slate-400 uppercase mb-1">Bäume-Anteil</p>
                                        <p class="text-xl font-black text-emerald-600">${(m.co2Netto / 12.5).toFixed(2)} 🌳</p>
                                    </div>
                                </div>
                            </div>
                        </details>
                        `;
                    }).join('')}
                </div>
            </details>
        </div>
    `;
};